import * as React from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import { Column } from "react-table";
import { toast } from "react-toastify";

import Checked from "components/icons/Checked";
import { CustomToastCloseButton } from "components/icons/CustomToastCloseButton";
import IndeterminateBox from "components/icons/IndeterminateBox";
import { SuccessIcon } from "components/icons/SuccessIcon";
import Unchecked from "components/icons/Unchecked";
import KebabMenu from "components/kebab-menu/KebabMenu";
import layoutStyle from "components/layout/layout.scss";
import { LicenseKeyMenuItems } from "components/licenses/assign-bms-keys/LicenseKeyMenuItems";
import { OEM_LICENSE_ID } from "components/licenses/common";
import filteringStyle from "components/licenses/filter-license-keys/license-key-filtering.scss";
import { LicenseKeysFiltering } from "components/licenses/filter-license-keys/LicenseKeysFiltering";
import { LicenseKeyActions } from "components/licenses/license-key-deletion/LicenseKeyActions";
import style from "components/licenses/license-keys-table.scss";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import tableStyle from "components/reports/erasure-reports-table.scss";
import SearchView from "components/search/SearchView";
import DateCell from "components/table/DateCell";
import Table from "components/table/Table";
import TextWithTooltip from "components/table/TextWithTooltip";
import { AUTH_LICENSE_KEY_MANAGEMENT, AUTH_LICENSE_POOL_VIEW } from "domain/authority";
import { SUPPORT_EMAIL } from "domain/globalConstants";
import { LicenseKeyFilter } from "domain/licenseKeys";
import { Key, KeyCursor, Keys, LicenseData } from "domain/licenses";
import { licenseService } from "services/licenses/LicenseService";
import { Action, Category, Label, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { hasTenantCookie, isCurrentUserInternal } from "services/tenants/tenantCookieService";
import { userSessionService } from "services/user/UserSessionService";
import { StoreState } from "store";
import { setHasLicenseSerialKey } from "store/license";
import buttonStyle from "styles/buttons.scss";
import filterStyle from "styles/layout.scss";
import { resolveColor } from "utils/commonFunctions";
import { logger } from "utils/logging";
import { RepositoryKey } from "utils/repository";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

interface TableState {
    licenseKey?: Key[];
    cursor?: KeyCursor | null;
    scrollPosition?: number;
}

interface Props {
    initialLicensekeys?: Keys;
    requestFailureMessage?: string;
    scrollPosition?: number;
    count?: number;
    setExportButtonVisibility: (visible: boolean) => void;
    filterType: LicenseKeyFilter;
    setFilterType: (filterType: LicenseKeyFilter) => void;
    setQuickSearch: (quickSearch: string) => void;
    quickSearch?: string;
}

const mapState = (state: StoreState) => ({
    theme: state.themeReducer.theme,
});

export interface Result {
    title: string;
    message: string | JSX.Element;
}

const connector = connect(mapState, {});

const SerialNumberKeysTable = (rawProps: Props & ConnectedProps<typeof connector>): JSX.Element => {
    const props = { initialLicensekeys: { licenseKeys: [], cursor: null }, requestFailureMessage: "", ...rawProps };
    const { t } = useTranslation();
    const { current: abortControllers } = React.useRef<AbortController[]>([]);
    const [initialLoading, setInitialLoading] = React.useState<boolean>(false);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [state, setLicensesData] = React.useState<TableState>({
        licenseKey: props.initialLicensekeys.licenseKeys?.map((key) => ({ ...key, checked: false })) ?? [],
        cursor: props.initialLicensekeys.cursor,
        scrollPosition: props.scrollPosition,
    });
    const [requestFailureMessage, setRequestFailureMessage] = React.useState<string>(props.requestFailureMessage);
    const [availableLicenses, setAvailableLicenses] = React.useState<LicenseData[]>([]);
    const [reloadLicenseKeys, setReloadLicenseKeys] = React.useState<boolean>(false);
    const theme = useTheme();
    const [deselected, setDeselected] = React.useState(false);
    const allLicenseKeysReference = React.useRef<Key[]>(state.licenseKey ?? []);
    const isDeleteLicenseKeysActive =
        userSessionService.userHasAllAuthorities([AUTH_LICENSE_KEY_MANAGEMENT]) && isCurrentUserInternal();
    const [okClicked, setOkClicked] = React.useState<boolean>(false);
    const [result, setResult] = React.useState<Result>({ title: "", message: "" });
    const showResult = (resultToShow: Result) => {
        setResult(resultToShow);
    };
    const isLicenseKeySelected = (licenseKey: string): boolean => {
        return (
            typeof allLicenseKeysReference.current.find((each) => each.licenseKey === licenseKey && each.checked) !==
            "undefined"
        );
    };
    const LICENSE_KEY_SELECTION_LIMIT = 1000;

    const isAnyLicenseKeySelected = (): boolean => {
        return typeof allLicenseKeysReference.current.find((each) => each.checked) !== "undefined";
    };

    const hasAllLicenseKeysSelected = (): boolean => {
        const selectableKeys = Math.min(allLicenseKeysReference.current.length, LICENSE_KEY_SELECTION_LIMIT);
        return (
            selectableKeys > 0 &&
            allLicenseKeysReference.current.slice(0, LICENSE_KEY_SELECTION_LIMIT).every((each) => each.checked)
        );
    };

    const updateSerialKeyState = (checkedValue: boolean) => {
        allLicenseKeysReference.current = allLicenseKeysReference.current.map((each, index) => {
            each.checked = checkedValue ? index < LICENSE_KEY_SELECTION_LIMIT : false;
            return each;
        });

        setLicensesData((prevState) => ({
            ...prevState,
            licenseKey: prevState.licenseKey?.map((each, index) => {
                each.checked = checkedValue ? index < LICENSE_KEY_SELECTION_LIMIT : false;
                return each;
            }),
        }));
    };
    const getSelectedCount = (): number => {
        return allLicenseKeysReference.current.filter((key) => key.checked).length;
    };

    const createSerialLicenseKeySelectionCheckbox = (licenseKey: string) => {
        const isSelected = isLicenseKeySelected(licenseKey);
        const selectedCount = getSelectedCount();
        const isDisabled = !isSelected && selectedCount >= LICENSE_KEY_SELECTION_LIMIT;
        return (
            <label className={tableStyle.container}>
                <input
                    data-testid={testIds.common.primaryView.table.selectRowCheckbox}
                    key={licenseKey}
                    name="licenseKeys"
                    id={licenseKey}
                    type="checkbox"
                    checked={isSelected && !deselected}
                    value={licenseKey}
                    disabled={isDisabled}
                    onChange={(e) => handleCheckboxChange(e, licenseKey)}
                />
                <span className={tableStyle.checkmark} />
            </label>
        );
    };

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>, licenseKey: string) => {
        setDeselected(false);
        if (event.target.checked) {
            const selectedCount = getSelectedCount();
            if (selectedCount >= LICENSE_KEY_SELECTION_LIMIT) {
                return;
            }
            updateTableStateBasedOnLicenseKey(true, licenseKey);
        } else {
            updateTableStateBasedOnLicenseKey(false, licenseKey);
        }
    };

    const updateTableStateBasedOnLicenseKey = (checkedValue: boolean, selectedLicenseKey: string) => {
        allLicenseKeysReference.current = allLicenseKeysReference.current.map((each) => {
            if (each.licenseKey === selectedLicenseKey) {
                each.checked = checkedValue;
            }
            return each;
        });

        setLicensesData((prevState) => ({
            ...prevState,
            licenseKey: prevState.licenseKey?.map((each) => {
                if (each.licenseKey === selectedLicenseKey) {
                    each.checked = checkedValue;
                }
                return each;
            }),
        }));
    };

    const fetchLicenses = async () => {
        const abortController = new AbortController();
        try {
            const licenseList = await licenseService.fetchAllLicenses({
                abortController: abortController,
                own: !hasTenantCookie(),
            });
            const ownLicenses = licenseList.licenses.licenseData.filter((license) => license.type === OEM_LICENSE_ID);
            setAvailableLicenses(ownLicenses);
            const hasLicenseType = licenseList.licenses.licenseData.some((license) => license.type === OEM_LICENSE_ID);
            setHasLicenseSerialKey(hasLicenseType);
        } catch {
            if (!abortController.signal.aborted) {
                logger.error("Failed to fetch licenses");
            }
        }
    };

    const fetchLicenseKeys = (initialLoading: boolean) => {
        setLoading(true);
        setInitialLoading(initialLoading);
        const abortController = new AbortController();
        abortControllers.push(abortController);
        licenseService
            .fetchLicenseKeys(
                abortController,
                OEM_LICENSE_ID,
                initialLoading ? "" : state.cursor?.licenseKey,
                initialLoading ? "" : state.cursor?.tenantIdentifier,
                initialLoading ? "" : state.cursor?.created,
                props.quickSearch,
                props.filterType
            )
            .then((licenseKey) => {
                let filteredKeys = licenseKey.licenseKeys.licenseKeys?.map((key) => ({ ...key, checked: false })) ?? [];
                if (props.filterType === LicenseKeyFilter.DISABLED) {
                    filteredKeys = filteredKeys.filter((key) => key.remaining === 0 || key.enabled === false);
                }

                setLicensesData((prevState) => {
                    const updatedKeys = initialLoading
                        ? filteredKeys
                        : (prevState.licenseKey?.concat(filteredKeys) ?? filteredKeys);
                    allLicenseKeysReference.current = updatedKeys; // Use the updated keys, not stale state

                    return {
                        ...prevState,
                        licenseKey: updatedKeys,
                        scrollPosition: initialLoading ? 0 : (prevState.licenseKey?.length ?? 0) - 1,
                        cursor: licenseKey.licenseKeys.cursor,
                    };
                });

                props.setExportButtonVisibility((filteredKeys?.length ?? 0) > 0);
                setLoading(false);
            })

            .catch(() => {
                if (!abortController.signal.aborted) {
                    setRequestFailureMessage(t("LicenseKeys.requestFailed"));
                }
            })
            .finally(() => {
                if (!abortController.signal.aborted) {
                    setLoading(false);
                    setInitialLoading(false);
                    if (okClicked) {
                        setOkClicked(false);
                    }
                }
            });
    };

    React.useEffect(() => {
        const loadData = async () => {
            setInitialLoading(true);
            await fetchLicenses();

            if ((props.initialLicensekeys.licenseKeys?.length ?? 0) > 0) {
                return;
            }
            setLicensesData({
                licenseKey: [],
                cursor: null,
                scrollPosition: 0,
            });
            fetchLicenseKeys(true);
            if (reloadLicenseKeys) {
                setReloadLicenseKeys(false);
            }
        };

        loadData();

        return () => {
            abortControllers.forEach((abortController) => abortController.abort());
        };
    }, [props.count, props.quickSearch, reloadLicenseKeys, props.filterType]);

    const serialNumberKeyColumns = React.useMemo(() => {
        const columns: Array<Column<Key>> = [];
        isDeleteLicenseKeysActive &&
            columns.push({
                Header: () => (
                    <div className={tableStyle.selectAllCheckbox}>
                        <label className={tableStyle.selectAllCheckboxContainer}>
                            <input
                                data-testid={testIds.common.primaryView.table.selectAllCheckbox}
                                type="checkbox"
                                checked={hasAllLicenseKeysSelected()}
                                onChange={(event) => {
                                    if (event.target.checked) {
                                        updateSerialKeyState(true);
                                    } else {
                                        updateSerialKeyState(false);
                                    }
                                }}
                            />
                            <div className={tableStyle.customCheckboxIcon}>
                                {isAnyLicenseKeySelected() ? (
                                    !hasAllLicenseKeysSelected() ? (
                                        <IndeterminateBox
                                            backgroundColor={resolveColor(
                                                "--app-buttons-buttonPrimary-backgroundColor"
                                            )}
                                            lineColor={resolveColor("--color-white")}
                                        />
                                    ) : (
                                        <Checked
                                            backgroundColor={resolveColor(
                                                "--app-buttons-buttonPrimary-backgroundColor"
                                            )}
                                            checkColor={resolveColor("--color-white")}
                                        />
                                    )
                                ) : (
                                    <Unchecked
                                        borderColor={resolveColor("--app-tables-tableRowDividerColor")}
                                        backgroundColor={resolveColor("--app-checkbox-customCheckboxIcon")}
                                    />
                                )}
                            </div>
                        </label>
                    </div>
                ),
                accessor: "checked",
                minWidth: 70,
                maxWidth: 70,
                Cell: ({
                    cell: {
                        row: { original: licenseKeys },
                    },
                }) => {
                    return (
                        <>
                            <div className={tableStyle.flexDisplay}>
                                {createSerialLicenseKeySelectionCheckbox(licenseKeys.licenseKey)}
                            </div>
                        </>
                    );
                },
            });
        columns.push(
            {
                Header: () => <TextWithTooltip text={t("LicenseKeys.keys")} key="serialNumberKey" />,
                accessor: "licenseKey",
                Cell: ({ cell: { value, row } }) => (
                    <div className={style.shortUrlCell}>
                        {isCurrentUserInternal() &&
                            availableLicenses &&
                            userSessionService.userHasAllAuthorities([AUTH_LICENSE_KEY_MANAGEMENT]) && (
                                <div className={style.kebabIcon}>
                                    <KebabMenu>
                                        <LicenseKeyMenuItems
                                            licenseKey={row.original}
                                            fromBms={false}
                                            availableLicenses={availableLicenses}
                                            setRequestFailureMessage={setRequestFailureMessage}
                                            setReloadLicenseKeys={setReloadLicenseKeys}
                                        />
                                    </KebabMenu>
                                </div>
                            )}
                        <TextWithTooltip text={value} disabled={!row.original.enabled} />
                    </div>
                ),
            },
            {
                Header: () => <TextWithTooltip text={t("LicenseKeys.identifier")} key="identifier" />,
                accessor: "identifier",
                Cell: ({ cell: { row, value } }) => <TextWithTooltip text={value} disabled={!row.original.enabled} />,
            },
            {
                Header: () => <TextWithTooltip text={t("LicenseKeys.assigned")} key="assigned" />,
                accessor: "assigned",
                Cell: ({ row, cell: { value } }) => (
                    <TextWithTooltip text={value.toString()} disabled={!row.original.enabled} />
                ),
            },
            {
                Header: () => <TextWithTooltip text={t("LicenseKeys.used")} key="used" />,
                accessor: "used",
                Cell: ({ row, cell: { value } }) => (
                    <TextWithTooltip text={value.toString()} disabled={!row.original.enabled} />
                ),
            },
            {
                Header: () => <TextWithTooltip text={t("LicenseKeys.remaining")} key="remaining" />,
                accessor: "remaining",
                Cell: ({ cell: { row, value } }) => (
                    <TextWithTooltip text={value.toString()} disabled={!row.original.enabled} />
                ),
            },
            {
                Header: () => <TextWithTooltip text={t("LicenseKeys.startDate")} key="startDate" />,
                accessor: "startDate",
                Cell: ({ row, cell: { value } }) => (
                    <DateCell withoutTime={true} tooltip={true} value={value} disabled={!row.original.enabled} />
                ),
            },
            {
                Header: () => <TextWithTooltip text={t("LicenseKeys.expirationDate")} key="expirationDate" />,
                accessor: "expirationDate",
                Cell: ({ row, cell: { value } }) => (
                    <DateCell withoutTime={true} tooltip={true} value={value} disabled={!row.original.enabled} />
                ),
            }
        );
        userSessionService.userHasAllAuthorities([AUTH_LICENSE_POOL_VIEW]) &&
            columns.push({
                Header: () => <TextWithTooltip text={t("LicensePools.view.poolName")} />,
                accessor: "poolName",
                Cell: ({ row }) => <TextWithTooltip text={row.original.poolName} disabled={!row.original.enabled} />,
            });
        return columns;
    }, [availableLicenses, t, state.licenseKey]);

    function findSelectedLicenseKeys(): string[] {
        return state.licenseKey?.filter((each) => each.checked).map((each) => each.licenseKey) ?? [];
    }

    function findSelectedLicenseKeysWithStatus(): { licenseKey: string; enabled: boolean }[] {
        return (
            state.licenseKey
                ?.filter((each) => each.checked)
                .map((each) => ({ licenseKey: each.licenseKey, enabled: each.enabled ?? true })) ?? []
        );
    }

    const handleEnableDisableKeys = async (action: "ENABLE" | "DISABLE") => {
        setLoading(true);
        const abortController = new AbortController();
        abortControllers.push(abortController);
        try {
            const selectedKeysWithStatus = findSelectedLicenseKeysWithStatus();
            const keysToUpdate = selectedKeysWithStatus
                .filter((key) => (action === "ENABLE" ? !key.enabled : key.enabled))
                .map((key) => key.licenseKey);

            if (keysToUpdate.length === 0) {
                setLoading(false);
                return;
            }

            await licenseService.updateLicenseKeysStatus(keysToUpdate, action === "ENABLE", abortController);
            const message =
                action === "ENABLE"
                    ? t("LicenseKeys.enabledSuccessMessage", { count: keysToUpdate.length })
                    : t("LicenseKeys.disabledSuccessMessage", { count: keysToUpdate.length });
            toast(
                <SuccessIcon
                    successClass={layoutStyle.customToastSuccessIcon}
                    color={theme.contentBackgroundColor}
                    text={message}
                />,
                {
                    closeButton: (closeToastProps) => (
                        <CustomToastCloseButton closeToast={{ ...closeToastProps }} color={theme.iconFillColor} />
                    ),
                    className: layoutStyle.customToastSuccessMessage,
                }
            );
            setLicensesData({
                licenseKey: [],
                cursor: null,
                scrollPosition: 0,
            });
        } catch {
            if (!abortController.signal.aborted) {
                const mailToLink = `mailto:${SUPPORT_EMAIL}`;
                showResult({
                    title: t("LicenseKeys.updateFailedTitle"),
                    message: (
                        <span>
                            {t("LicenseKeys.updateFailedMessage")}
                            <a href={mailToLink}>{t("DeleteLicenseKey.supportMessage")} </a>
                        </span>
                    ),
                });
            }
        } finally {
            setLoading(false);
            fetchLicenseKeys(true);
        }
    };

    React.useEffect(() => {
        allLicenseKeysReference.current = state.licenseKey ?? [];
    }, [state.licenseKey]);

    React.useEffect(() => {
        if (okClicked) {
            setLicensesData({
                licenseKey: [],
                cursor: null,
                scrollPosition: 0,
            });
            fetchLicenseKeys(true);
            setOkClicked(false);
        }
    }, [okClicked, reloadLicenseKeys]);

    const selectionInformation = (
        <LicenseKeyActions
            selectedCount={findSelectedLicenseKeys().length}
            onDeselect={() => {
                setLicensesData((prevState) => ({
                    ...prevState,
                    licenseKey: prevState.licenseKey?.map((each) => {
                        if (each.checked) {
                            each.checked = false;
                        }
                        return each;
                    }),
                }));
                allLicenseKeysReference.current = allLicenseKeysReference.current.map((each) => {
                    if (each.checked) {
                        each.checked = false;
                    }
                    return each;
                });

                setDeselected(true);
            }}
            onEnableDisable={handleEnableDisableKeys}
            onDeleteSuccess={() => {
                setReloadLicenseKeys(true);
                setOkClicked(false);
            }}
            onDeleteStart={() => setOkClicked(true)}
            getSelectedKeys={findSelectedLicenseKeys}
            getSelectedKeysWithStatus={findSelectedLicenseKeysWithStatus}
            testIdPrefix="serialNumber"
            result={result}
            onDeleteError={() => {
                setOkClicked(false);
                setLicensesData((prevState) => ({
                    ...prevState,
                    licenseKey: prevState.licenseKey?.map((each) => {
                        if (each.checked) {
                            each.checked = false;
                        }
                        return each;
                    }),
                }));
                allLicenseKeysReference.current = allLicenseKeysReference.current.map((each) => {
                    if (each.checked) {
                        each.checked = false;
                    }
                    return each;
                });

                setDeselected(true);
            }}
            abortControllers={React.useRef(abortControllers)}
        />
    );

    return (
        <>
            <div className={filteringStyle.tableHeader}>
                <LicenseKeysFiltering
                    setFilterType={props.setFilterType}
                    filterType={props.filterType}
                    resultsCount={state.licenseKey?.length || 0}
                />
                {isAnyLicenseKeySelected() && (
                    <div className={filteringStyle.searchContainer}>
                        {selectionInformation}
                        <SearchView setSearch={props.setQuickSearch} searchInProgress={false} />
                    </div>
                )}
                {!isAnyLicenseKeySelected() && <SearchView setSearch={props.setQuickSearch} searchInProgress={false} />}
            </div>

            <div className={filterStyle.tableWrapper}>
                <Table
                    tableIdentity={RepositoryKey.LICENSE_TABLE}
                    data={state.licenseKey ?? []}
                    columns={serialNumberKeyColumns}
                    loaded={!initialLoading}
                    failureMessage={requestFailureMessage}
                    scrollTo={state.scrollPosition}
                    tooltips={true}
                    emptyMessage={t("LicenseKeys.emptyStateMessage", { email: SUPPORT_EMAIL })}
                    testId={testIds.workArea.license.serialNumber.table.itself}
                />
            </div>
            {state.cursor != null &&
                (state.licenseKey?.length ?? 0) != 0 &&
                requestFailureMessage === "" &&
                (loading ? (
                    <LoadingIndicator small={true} withTable />
                ) : (
                    <button
                        className={buttonStyle.loadMoreButtonWithoutIcon}
                        onClick={() => {
                            fetchLicenseKeys(false);
                            usageStatisticsService.sendEvent({
                                label: Label.SERIAL_NUMBER_KEYS,
                                action: Action.LOAD_MORE,
                                category: Category.LICENSE,
                            });
                        }}
                        data-testid={testIds.common.primaryView.table.loadMoreButton}
                    >
                        {t("Common.loadMore")}
                    </button>
                ))}
        </>
    );
};

export default connector(SerialNumberKeysTable);
